import React, { useState, useEffect } from 'react';
import { 
  X, User, Lock, MessageCircle, CheckCircle, AlertCircle, 
  Loader2, Link2, Unlink, Bell, BellOff, Shield, Users, Mail,
  ExternalLink
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { db } from '../firebaseConfig';
import { doc, updateDoc, onSnapshot } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { app } from '../firebaseConfig';

interface ProfileModalProps {
  onClose: () => void;
}

type TabType = 'profile' | 'security' | 'line';

const ProfileModal: React.FC<ProfileModalProps> = ({ onClose }) => {
  const { currentUser, changePassword } = useAuth();
  const { t, language } = useLanguage();
  
  const [activeTab, setActiveTab] = useState<TabType>('profile');
  
  // Password change state
  const [passwordData, setPasswordData] = useState({ current: '', new: '', confirm: '' });
  const [passwordError, setPasswordError] = useState('');
  const [passwordSuccess, setPasswordSuccess] = useState(false);
  const [passwordLoading, setPasswordLoading] = useState(false);

  // LINE linking state
  const [linkingCode, setLinkingCode] = useState('');
  const [isLinking, setIsLinking] = useState(false);
  const [isUnlinking, setIsUnlinking] = useState(false);
  const [isTogglingNotifications, setIsTogglingNotifications] = useState(false);
  const [lineError, setLineError] = useState('');
  const [lineSuccess, setLineSuccess] = useState('');
  
  // LINE status
  const [lineUserId, setLineUserId] = useState<string | null>(null);
  const [lineNotificationsEnabled, setLineNotificationsEnabled] = useState(true);
  const [lineLinkedAt, setLineLinkedAt] = useState<Date | null>(null);

  useEffect(() => {
    if (!currentUser?.id) return;
    const unsubscribe = onSnapshot(doc(db, 'users', currentUser.id), (doc) => {
      if (doc.exists()) {
        const data = doc.data();
        setLineUserId(data.lineUserId || null);
        setLineNotificationsEnabled(data.lineNotificationsEnabled !== false);
        setLineLinkedAt(data.lineLinkedAt?.toDate() || null);
      }
    });
    return () => unsubscribe();
  }, [currentUser?.id]);

  const handlePasswordChange = async () => {
    setPasswordError('');
    setPasswordSuccess(false);
    setPasswordLoading(true);
    
    if (!passwordData.current.trim()) {
      setPasswordError(language === 'th' ? 'กรุณากรอกรหัสผ่านปัจจุบัน' : 'Please enter current password');
      setPasswordLoading(false);
      return;
    }
    if (!passwordData.new.trim()) {
      setPasswordError(language === 'th' ? 'กรุณากรอกรหัสผ่านใหม่' : 'Please enter new password');
      setPasswordLoading(false);
      return;
    }
    if (passwordData.new !== passwordData.confirm) {
      setPasswordError(t('password.mismatch'));
      setPasswordLoading(false);
      return;
    }
    if (passwordData.new.length < 6) {
      setPasswordError(t('password.min.length'));
      setPasswordLoading(false);
      return;
    }
    
    try {
      const result = await changePassword(passwordData.current, passwordData.new);
      if (result.success) {
        setPasswordSuccess(true);
        setPasswordData({ current: '', new: '', confirm: '' });
      } else {
        setPasswordError(result.error || t('error.generic'));
      }
    } catch (error: any) {
      setPasswordError(t('error.generic'));
    }
    setPasswordLoading(false);
  };

  const handleLinkAccount = async () => {
    if (!linkingCode.trim() || linkingCode.length < 6) {
      setLineError(language === 'th' ? 'กรุณากรอกรหัส 6 หลัก' : 'Please enter 6-digit code');
      return;
    }
    setIsLinking(true);
    setLineError('');
    setLineSuccess('');

    try {
      const functions = getFunctions(app);
      const verifyCode = httpsCallable(functions, 'verifyLineLinkingCode');
      const result = await verifyCode({ linkingCode: linkingCode.toUpperCase() });
      const data = result.data as { success: boolean; message?: string };
      
      if (data.success) {
        setLineSuccess(language === 'th' ? 'เชื่อมต่อสำเร็จ!' : 'Successfully linked!');
        setLinkingCode('');
      } else {
        setLineError(data.message || (language === 'th' ? 'เกิดข้อผิดพลาด' : 'An error occurred'));
      }
    } catch (err: any) {
      if (err.code === 'functions/not-found') {
        setLineError(language === 'th' ? 'รหัสไม่ถูกต้อง' : 'Invalid code');
      } else if (err.code === 'functions/deadline-exceeded') {
        setLineError(language === 'th' ? 'รหัสหมดอายุ' : 'Code expired');
      } else if (err.code === 'functions/already-exists') {
        setLineError(language === 'th' ? 'รหัสถูกใช้แล้ว' : 'Code already used');
      } else {
        setLineError(language === 'th' ? 'เกิดข้อผิดพลาด' : 'An error occurred');
      }
    } finally {
      setIsLinking(false);
    }
  };

  const handleUnlinkAccount = async () => {
    if (!currentUser?.id) return;
    if (!window.confirm(language === 'th' ? 'ยกเลิกการเชื่อมต่อ LINE?' : 'Unlink LINE account?')) return;

    setIsUnlinking(true);
    setLineError('');
    setLineSuccess('');

    try {
      await updateDoc(doc(db, 'users', currentUser.id), {
        lineUserId: null,
        lineNotificationsEnabled: false,
        lineLinkedAt: null
      });
      setLineSuccess(language === 'th' ? 'ยกเลิกการเชื่อมต่อแล้ว' : 'Account unlinked');
    } catch (err) {
      setLineError(language === 'th' ? 'เกิดข้อผิดพลาด' : 'An error occurred');
    } finally {
      setIsUnlinking(false);
    }
  };

  const handleToggleNotifications = async () => {
    if (!currentUser?.id || !lineUserId) return;
    setIsTogglingNotifications(true);
    try {
      await updateDoc(doc(db, 'users', currentUser.id), {
        lineNotificationsEnabled: !lineNotificationsEnabled
      });
    } catch (err) {
      setLineError(language === 'th' ? 'เกิดข้อผิดพลาด' : 'An error occurred');
    } finally {
      setIsTogglingNotifications(false);
    }
  };

  const tabs = [
    { id: 'profile' as TabType, label: language === 'th' ? 'โปรไฟล์' : 'Profile', icon: User },
    { id: 'security' as TabType, label: language === 'th' ? 'ความปลอดภัย' : 'Security', icon: Lock },
    { id: 'line' as TabType, label: 'LINE', icon: MessageCircle },
  ];

  return (
    <div className="modal-overlay">
      <div className="modal-content" style={{ maxWidth: '500px', width: '500px', height: '680px', overflow: 'hidden', padding: 0 }}>
        {/* Header */}
        <div style={{ 
          background: 'linear-gradient(135deg, #1e293b 0%, #334155 100%)',
          padding: '24px',
          position: 'relative'
        }}>
          <button 
            onClick={onClose} 
            style={{
              position: 'absolute',
              top: '16px',
              right: '16px',
              padding: '8px',
              borderRadius: '50%',
              background: 'rgba(255,255,255,0.1)',
              border: 'none',
              cursor: 'pointer',
              color: 'white'
            }}
          >
            <X style={{ width: '20px', height: '20px' }} />
          </button>
          
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
            <div style={{
              width: '64px',
              height: '64px',
              borderRadius: '16px',
              background: 'linear-gradient(135deg, #3b82f6 0%, #6366f1 100%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: '0 4px 12px rgba(59, 130, 246, 0.4)'
            }}>
              <span style={{ fontSize: '24px', fontWeight: 'bold', color: 'white' }}>
                {currentUser?.fullName?.charAt(0).toUpperCase() || 'U'}
              </span>
            </div>
            <div>
              <h2 style={{ fontSize: '20px', fontWeight: 'bold', color: 'white', margin: 0 }}>
                {currentUser?.fullName}
              </h2>
              <p style={{ color: '#94a3b8', fontSize: '14px', margin: '4px 0 0 0' }}>
                @{currentUser?.username}
              </p>
              <div style={{ display: 'flex', gap: '8px', marginTop: '8px' }}>
                <span style={{
                  padding: '4px 10px',
                  borderRadius: '20px',
                  fontSize: '12px',
                  fontWeight: '600',
                  background: 'rgba(59, 130, 246, 0.2)',
                  color: '#93c5fd',
                  border: '1px solid rgba(59, 130, 246, 0.3)'
                }}>
                  {currentUser?.role ? t(`user.${currentUser.role}`) : ''}
                </span>
                {lineUserId && (
                  <span style={{
                    padding: '4px 10px',
                    borderRadius: '20px',
                    fontSize: '12px',
                    fontWeight: '600',
                    background: 'rgba(34, 197, 94, 0.2)',
                    color: '#86efac',
                    border: '1px solid rgba(34, 197, 94, 0.3)',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px'
                  }}>
                    <MessageCircle style={{ width: '12px', height: '12px' }} /> LINE
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="profile-tabs-bar" style={{ display: 'flex' }}>
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`profile-tab${activeTab === tab.id ? ' active' : ''}`}
              style={{
                flex: 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px',
                padding: '14px 16px',
                fontSize: '14px',
                fontWeight: '500',
                cursor: 'pointer',
                transition: 'all 0.2s'
              }}
            >
              <tab.icon style={{ width: '16px', height: '16px' }} />
              <span>{tab.label}</span>
            </button>
          ))}
        </div>

        {/* Content */}
        <div style={{ padding: '24px', overflowY: 'auto', height: '400px', flexShrink: 0 }}>
          
          {/* Profile Tab */}
          {activeTab === 'profile' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              {[
                { icon: Mail, label: language === 'th' ? 'อีเมล' : 'Email', value: currentUser?.email },
                { icon: Users, label: t('user.team'), value: currentUser?.team },
                { icon: Shield, label: t('user.role'), value: currentUser?.role ? t(`user.${currentUser.role}`) : '-' },
                { icon: User, label: t('auth.username'), value: `@${currentUser?.username}` },
              ].map((item, i) => (
                <div key={i} className="profile-surface" style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '16px',
                  padding: '16px',
                  borderRadius: '12px'
                }}>
                  <div className="profile-icon-tile" style={{
                    width: '40px',
                    height: '40px',
                    borderRadius: '10px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}>
                    <item.icon style={{ width: '20px', height: '20px', color: '#94a3b8' }} />
                  </div>
                  <div>
                    <p style={{ fontSize: '11px', color: '#94a3b8', fontWeight: '500', textTransform: 'uppercase', letterSpacing: '0.5px', margin: 0 }}>
                      {item.label}
                    </p>
                    <p className="profile-value-text" style={{ fontSize: '15px', fontWeight: '600', margin: '2px 0 0 0' }}>
                      {item.value || '-'}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Security Tab */}
          {activeTab === 'security' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
              <div className="profile-warning-box" style={{
                padding: '16px',
                borderRadius: '12px',
                display: 'flex',
                alignItems: 'flex-start',
                gap: '12px'
              }}>
                <Lock className="profile-warning-icon" style={{ width: '20px', height: '20px', marginTop: '2px' }} />
                <div>
                  <h4 className="profile-warning-title" style={{ fontWeight: '600', margin: 0 }}>
                    {language === 'th' ? 'เปลี่ยนรหัสผ่าน' : 'Change Password'}
                  </h4>
                  <p className="profile-warning-text" style={{ fontSize: '14px', margin: '4px 0 0 0' }}>
                    {language === 'th' ? 'รหัสผ่านต้องมีอย่างน้อย 6 ตัวอักษร' : 'Minimum 6 characters'}
                  </p>
                </div>
              </div>

              <form onSubmit={(e) => { e.preventDefault(); handlePasswordChange(); }} style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                {[
                  { id: 'current', label: t('password.current'), value: passwordData.current },
                  { id: 'new', label: t('password.new'), value: passwordData.new },
                  { id: 'confirm', label: t('password.confirm'), value: passwordData.confirm },
                ].map((field) => (
                  <div key={field.id}>
                    <label className="profile-field-label" style={{ display: 'block', fontSize: '14px', fontWeight: '500', marginBottom: '6px' }}>
                      {field.label} <span style={{ color: '#ef4444' }}>*</span>
                    </label>
                    <input
                      type="password"
                      value={field.value}
                      onChange={(e) => setPasswordData(prev => ({ ...prev, [field.id]: e.target.value }))}
                      placeholder="••••••••"
                      className="profile-input"
                      style={{
                        width: '100%',
                        padding: '12px 16px',
                        borderRadius: '10px',
                        fontSize: '14px',
                        outline: 'none',
                        boxSizing: 'border-box'
                      }}
                    />
                  </div>
                ))}

                {passwordError && (
                  <div className="profile-error-box" style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    padding: '12px',
                    borderRadius: '10px',
                    fontSize: '14px'
                  }}>
                    <AlertCircle style={{ width: '16px', height: '16px' }} />
                    {passwordError}
                  </div>
                )}

                {passwordSuccess && (
                  <div className="profile-success-box" style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    padding: '12px',
                    borderRadius: '10px',
                    fontSize: '14px'
                  }}>
                    <CheckCircle style={{ width: '16px', height: '16px' }} />
                    {t('password.change.success')}
                  </div>
                )}

                <button 
                  type="submit" 
                  disabled={passwordLoading}
                  style={{
                    width: '100%',
                    padding: '14px',
                    background: 'linear-gradient(135deg, #2563eb 0%, #4f46e5 100%)',
                    color: 'white',
                    border: 'none',
                    borderRadius: '10px',
                    fontWeight: '600',
                    fontSize: '14px',
                    cursor: passwordLoading ? 'not-allowed' : 'pointer',
                    opacity: passwordLoading ? 0.6 : 1,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '8px',
                    boxShadow: '0 4px 12px rgba(37, 99, 235, 0.25)'
                  }}
                >
                  {passwordLoading && <Loader2 style={{ width: '16px', height: '16px', animation: 'spin 1s linear infinite' }} />}
                  {t('user.change.password')}
                </button>
              </form>
            </div>
          )}

          {/* LINE Tab */}
          {activeTab === 'line' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
              {lineError && (
                <div className="profile-error-box" style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  padding: '12px',
                  borderRadius: '10px',
                  fontSize: '14px'
                }}>
                  <AlertCircle style={{ width: '16px', height: '16px' }} />
                  {lineError}
                </div>
              )}

              {lineSuccess && (
                <div className="profile-success-box" style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  padding: '12px',
                  borderRadius: '10px',
                  fontSize: '14px'
                }}>
                  <CheckCircle style={{ width: '16px', height: '16px' }} />
                  {lineSuccess}
                </div>
              )}

              {lineUserId ? (
                /* Connected State */
                <>
                  <div className="profile-line-connected" style={{
                    padding: '20px',
                    borderRadius: '16px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '16px'
                  }}>
                    <div style={{
                      width: '56px',
                      height: '56px',
                      background: '#22c55e',
                      borderRadius: '16px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      boxShadow: '0 4px 12px rgba(34, 197, 94, 0.3)'
                    }}>
                      <CheckCircle style={{ width: '28px', height: '28px', color: 'white' }} />
                    </div>
                    <div>
                      <p className="profile-line-connected-title" style={{ fontWeight: 'bold', fontSize: '18px', margin: 0 }}>
                        {language === 'th' ? 'เชื่อมต่อแล้ว' : 'Connected'}
                      </p>
                      {lineLinkedAt && (
                        <p className="profile-line-connected-date" style={{ fontSize: '14px', margin: '4px 0 0 0' }}>
                          {lineLinkedAt.toLocaleDateString(language === 'th' ? 'th-TH' : 'en-US', {
                            year: 'numeric', month: 'long', day: 'numeric'
                          })}
                        </p>
                      )}
                    </div>
                  </div>

                  <div className="profile-surface" style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: '16px',
                    borderRadius: '12px'
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                      {lineNotificationsEnabled ?
                        <Bell style={{ width: '20px', height: '20px', color: '#2563eb' }} /> :
                        <BellOff style={{ width: '20px', height: '20px', color: '#94a3b8' }} />
                      }
                      <div>
                        <p className="profile-value-text" style={{ fontWeight: '500', margin: 0 }}>
                          {language === 'th' ? 'การแจ้งเตือน' : 'Notifications'}
                        </p>
                        <p style={{ fontSize: '12px', color: '#64748b', margin: '2px 0 0 0' }}>
                          {lineNotificationsEnabled 
                            ? (language === 'th' ? 'เปิดอยู่' : 'Enabled') 
                            : (language === 'th' ? 'ปิดอยู่' : 'Disabled')
                          }
                        </p>
                      </div>
                    </div>
                    <button
                      onClick={handleToggleNotifications}
                      disabled={isTogglingNotifications}
                      style={{
                        width: '48px',
                        height: '28px',
                        borderRadius: '14px',
                        background: lineNotificationsEnabled ? '#2563eb' : '#cbd5e1',
                        border: 'none',
                        cursor: 'pointer',
                        position: 'relative',
                        transition: 'background 0.2s'
                      }}
                    >
                      <div style={{
                        position: 'absolute',
                        top: '4px',
                        left: lineNotificationsEnabled ? '24px' : '4px',
                        width: '20px',
                        height: '20px',
                        background: 'white',
                        borderRadius: '50%',
                        boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
                        transition: 'left 0.2s'
                      }} />
                    </button>
                  </div>

                  <button
                    onClick={handleUnlinkAccount}
                    disabled={isUnlinking}
                    className="profile-unlink-btn"
                    style={{
                      width: '100%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '8px',
                      padding: '14px',
                      borderRadius: '10px',
                      fontWeight: '600',
                      cursor: 'pointer'
                    }}
                  >
                    {isUnlinking ? <Loader2 style={{ width: '16px', height: '16px', animation: 'spin 1s linear infinite' }} /> : <Unlink style={{ width: '16px', height: '16px' }} />}
                    {language === 'th' ? 'ยกเลิกการเชื่อมต่อ' : 'Unlink Account'}
                  </button>
                </>
              ) : (
                /* Not Connected State */
                <>
                  <div style={{ textAlign: 'center' }}>
                    {/* QR Code */}
                    <div style={{
                      display: 'inline-block',
                      padding: '12px',
                      background: 'white',
                      borderRadius: '16px',
                      boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                      border: '1px solid #e2e8f0'
                    }}>
                      <img 
                        src="https://qr-official.line.me/gs/M_629rfmlu_BW.png" 
                        alt="LINE QR Code" 
                        style={{ width: '140px', height: '140px', display: 'block' }}
                      />
                    </div>
                    
                    {/* Add Friend Button */}
                    <div style={{ marginTop: '16px' }}>
                      <a 
                        href="https://lin.ee/TXtyqNq" 
                        target="_blank" 
                        rel="noopener noreferrer"
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '8px',
                          padding: '10px 24px',
                          background: '#06C755',
                          color: 'white',
                          borderRadius: '24px',
                          fontWeight: '600',
                          textDecoration: 'none',
                          boxShadow: '0 4px 12px rgba(6, 199, 85, 0.3)'
                        }}
                      >
                        <svg style={{ width: '20px', height: '20px' }} viewBox="0 0 24 24" fill="currentColor">
                          <path d="M19.365 9.863c.349 0 .63.285.63.631 0 .345-.281.63-.63.63H17.61v1.125h1.755c.349 0 .63.283.63.63 0 .344-.281.629-.63.629h-2.386c-.345 0-.627-.285-.627-.629V8.108c0-.345.282-.63.63-.63h2.386c.346 0 .627.285.627.63 0 .349-.281.63-.63.63H17.61v1.125h1.755zm-3.855 3.016c0 .27-.174.51-.432.596-.064.021-.133.031-.199.031-.211 0-.391-.09-.51-.25l-2.443-3.317v2.94c0 .344-.279.629-.631.629-.346 0-.626-.285-.626-.629V8.108c0-.27.173-.51.43-.595.06-.023.136-.033.194-.033.195 0 .375.104.495.254l2.462 3.33V8.108c0-.345.282-.63.63-.63.345 0 .63.285.63.63v4.771zm-5.741 0c0 .344-.282.629-.631.629-.345 0-.627-.285-.627-.629V8.108c0-.345.282-.63.63-.63.346 0 .628.285.628.63v4.771zm-2.466.629H4.917c-.345 0-.63-.285-.63-.629V8.108c0-.345.285-.63.63-.63.348 0 .63.285.63.63v4.141h1.756c.348 0 .629.283.629.63 0 .344-.282.629-.629.629M24 10.314C24 4.943 18.615.572 12 .572S0 4.943 0 10.314c0 4.811 4.27 8.842 10.035 9.608.391.082.923.258 1.058.59.12.301.079.766.038 1.08l-.164 1.02c-.045.301-.24 1.186 1.049.645 1.291-.539 6.916-4.078 9.436-6.975C23.176 14.393 24 12.458 24 10.314"/>
                        </svg>
                        {language === 'th' ? 'เพิ่มเพื่อน' : 'Add Friend'}
                        <ExternalLink style={{ width: '14px', height: '14px' }} />
                      </a>
                    </div>
                  </div>

                  {/* Instructions */}
                  <div className="profile-info-box" style={{
                    padding: '16px',
                    borderRadius: '12px'
                  }}>
                    <h4 className="profile-info-title" style={{
                      fontWeight: '600',
                      margin: '0 0 12px 0',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      fontSize: '14px'
                    }}>
                      <MessageCircle style={{ width: '16px', height: '16px' }} />
                      {language === 'th' ? 'วิธีเชื่อมต่อ' : 'How to connect'}
                    </h4>
                    <ol style={{ margin: 0, paddingLeft: '0', listStyle: 'none' }}>
                      {[
                        language === 'th' ? 'สแกน QR หรือกดปุ่ม "เพิ่มเพื่อน"' : 'Scan QR or click "Add Friend"',
                        language === 'th' ? 'พิมพ์ "link" ในแชท LINE' : 'Type "link" in LINE chat',
                        language === 'th' ? 'กรอกรหัส 6 หลักด้านล่าง' : 'Enter 6-digit code below'
                      ].map((text, i) => (
                        <li key={i} className="profile-info-item" style={{
                          display: 'flex',
                          gap: '10px',
                          marginBottom: i < 2 ? '8px' : 0,
                          fontSize: '14px'
                        }}>
                          <span className="profile-info-step" style={{
                            width: '20px',
                            height: '20px',
                            borderRadius: '50%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: '12px',
                            fontWeight: 'bold',
                            flexShrink: 0
                          }}>{i + 1}</span>
                          <span>{text}</span>
                        </li>
                      ))}
                    </ol>
                  </div>

                  {/* Code Input */}
                  <div>
                    <label className="profile-field-label" style={{ display: 'block', fontSize: '14px', fontWeight: '500', marginBottom: '8px' }}>
                      {language === 'th' ? 'รหัสเชื่อมต่อ' : 'Linking Code'}
                    </label>
                    <div style={{ display: 'flex', gap: '12px' }}>
                      <input
                        type="text"
                        value={linkingCode}
                        onChange={(e) => setLinkingCode(e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, ''))}
                        placeholder="ABC123"
                        maxLength={6}
                        className="profile-code-input"
                        style={{
                          flex: 1,
                          padding: '14px 16px',
                          borderRadius: '10px',
                          textAlign: 'center',
                          fontSize: '20px',
                          fontFamily: 'monospace',
                          fontWeight: 'bold',
                          letterSpacing: '0.25em',
                          textTransform: 'uppercase',
                          outline: 'none',
                          boxSizing: 'border-box'
                        }}
                      />
                      <button
                        onClick={handleLinkAccount}
                        disabled={isLinking || linkingCode.length < 6}
                        style={{
                          padding: '14px 20px',
                          background: isLinking || linkingCode.length < 6 
                            ? '#cbd5e1' 
                            : 'linear-gradient(135deg, #22c55e 0%, #16a34a 100%)',
                          color: 'white',
                          border: 'none',
                          borderRadius: '10px',
                          cursor: isLinking || linkingCode.length < 6 ? 'not-allowed' : 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '6px',
                          boxShadow: isLinking || linkingCode.length < 6 ? 'none' : '0 4px 12px rgba(34, 197, 94, 0.3)'
                        }}
                      >
                        {isLinking 
                          ? <Loader2 style={{ width: '20px', height: '20px', animation: 'spin 1s linear infinite' }} /> 
                          : <Link2 style={{ width: '20px', height: '20px' }} />
                        }
                      </button>
                    </div>
                  </div>

                  {/* Info */}
                  <p style={{ 
                    fontSize: '12px', 
                    color: '#94a3b8', 
                    textAlign: 'center',
                    margin: 0
                  }}>
                    {language === 'th' 
                      ? 'รหัสมีอายุ 10 นาที • รับการแจ้งเตือนรายการ, งบประมาณ และอื่นๆ'
                      : 'Code expires in 10 min • Get notifications for items, budget & more'}
                  </p>
                </>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProfileModal;
